<template>
  <b-form novalidate @submit.prevent="submit" class="ml-auto mr-auto">
    <b-row>
      <b-col v-if="showError || showSaveMoreOneError" class="col-12 col-md-6 mb-3">
        <b-card>
          <p v-if="showSaveMoreOneError" class="text-center">{{ $t('measure.form.save_all_error') }}</p>
          <p v-if="showError === 'save'" class="text-center">{{ $t('measure.form.error') }}</p>
          <p v-if="showError === 'not found'" class="text-center">{{ $t('measure.form.not_found') }}</p>
        </b-card>
      </b-col>
      <b-col v-if="!loaded && !showError" class="col-12 col-md-12 mb-3">
        <b-card>
          <div class="d-flex justify-content-center mb-3 mt-3">
            <b-spinner></b-spinner>
          </div>
        </b-card>
      </b-col>
    </b-row>
    <template v-if="showError !== 'not found'">
      <b-form-group label-for="date">
        <datepicker format="dd.MM.yyyy" :language="de" :placeholder="$t('measure.form.date.label')" :input-class="{ 'with-placeholder': !form.date, 'bg-light': true }" id="date" v-model="form.date" :bootstrap-styling="true" required :state="$v.form.date.$dirty ? !$v.form.date.$error : null" @blur="$v.form.date.$touch"></datepicker>
      </b-form-group>
      <b-form-group label-for="type">
        <b-form-select :disabled="!!id" id="type" :class="{ 'with-placeholder': !form.type }" v-model="form.type">
          <option :value="null" disabled>{{ $t('measure.form.type.label') }}</option>
          <option v-for="(text, value) in $t('measure.types')" :value="value" :key="value">{{ text }}</option>
        </b-form-select>
      </b-form-group>
      <b-form-group v-if="form.type === 'massnahme_varroa_diagnose'" label-for="diagnosis">
        <b-form-select id="diagnosis" :class="{ 'with-placeholder': !form.diagnosis }" v-model="form.diagnosis">
          <option :value="null" disabled>{{ $t('measure.form.diagnosis.label') }}</option>
          <option v-for="(text, value) in $t('measure.form.diagnosis.options')" :value="value" :key="value">{{ text }}</option>
        </b-form-select>
      </b-form-group>
      <b-form-group v-if="form.type === 'massnahme_varroa_diagnose'" label-for="mites">
        <b-form-input :placeholder="$t('measure.form.mites.label')" :class="{ 'with-placeholder': !form.mites }" id="mites" v-model="form.mites"></b-form-input>
      </b-form-group>
      <b-form-group v-if="form.type === 'massnahme_varroa_diagnose' && form.diagnosis === 'bed'" label-for="days">
        <b-form-input :placeholder="$t('measure.form.days.label')" :class="{ 'with-placeholder': !form.days }" id="days" v-model="form.days"></b-form-input>
      </b-form-group>
      <div v-if="form.type === 'massnahme_varroa_diagnose' && form.diagnosis === 'bed' && mitesDayPercentage">
        <b-alert show variant="primary" class="text-center" >{{ mitesDayPercentage }}</b-alert>
      </div>
      <div v-if="form.type === 'massnahme_varroa_diagnose' && form.diagnosis === 'bed' && mitesDayPercentage && !mitesDayHasProblem">
        <b-alert show variant="warning" class="text-center">{{ $t('measure.form.mites_day_percentage.no_problem_msg') }}</b-alert>
      </div>
      <div v-if="form.type === 'massnahme_varroa_diagnose' && form.diagnosis === 'bed' && mitesDayPercentage && mitesDayHasProblem">
        <b-alert show variant="danger" class="text-center">{{ $t('measure.form.mites_day_percentage.has_problem_msg') }}</b-alert>
      </div>
      <b-form-group v-if="form.type === 'massnahme_varroa_diagnose' && form.diagnosis === 'washout'" label-for="bee_mass">
        <b-form-input :placeholder="$t('measure.form.bee_mass.label')" :class="{ 'with-placeholder': !form.bee_mass }" id="bee_mass" v-model="form.bee_mass"></b-form-input>
      </b-form-group>
      <div v-if="form.type === 'massnahme_varroa_diagnose' && form.diagnosis === 'washout' && mitesBeeMassPercentage">
        <b-alert show variant="primary" class="text-center">{{ mitesBeeMassPercentage }}</b-alert>
      </div>
      <div v-if="form.type === 'massnahme_varroa_diagnose' && form.diagnosis === 'washout' && mitesBeeMassPercentage && mitesBeeMassHasProblem === 0">
        <b-alert show variant="warning" class="text-center">{{ $t('measure.form.mites_bee_mass_percentage.no_problem_msg') }}</b-alert>
      </div>
      <div v-if="form.type === 'massnahme_varroa_diagnose' && form.diagnosis === 'washout' && mitesBeeMassPercentage && mitesBeeMassHasProblem === 1">
        <b-alert show variant="danger" class="text-center">{{ $t('measure.form.mites_bee_mass_percentage.has_problem_msg') }}</b-alert>
      </div>
      <div v-if="form.type === 'massnahme_varroa_diagnose' && form.diagnosis === 'washout' && mitesBeeMassPercentage && mitesBeeMassHasProblem === 2">
        <b-alert show variant="warning" class="text-center">{{ $t('measure.form.mites_bee_mass_percentage.no_evaluation_msg') }}</b-alert>
      </div>
      <b-form-group v-if="form.type === 'massnahme_varroa_medikamente'" label-for="medicine">
        <b-form-select id="medicine" :class="{ 'with-placeholder': !form.medicine }" v-model="form.medicine">
          <option :value="null" disabled>{{ $t('measure.form.medicine.label') }}</option>
          <option v-for="(text, value) in $t('measure.form.medicine.options')" :value="value" :key="value">{{ text }}</option>
        </b-form-select>
      </b-form-group>
      <b-form-group v-if="form.type === 'massnahme_varroa_medikamente' && form.medicine === 'other'" label-for="other_medicine">
        <b-form-input :placeholder="$t('measure.form.other_medicine.label')" :class="{ 'with-placeholder': !form.other_medicine }" id="other_medicine" v-model="form.other_medicine"></b-form-input>
      </b-form-group>
      <b-form-group v-if="form.type === 'massnahme_varroa_medikamente'" label-for="amount">
        <b-form-input :placeholder="$t('measure.form.amount.label_ml')" :class="{ 'with-placeholder': !form.amount }" id="amount" v-model="form.amount" :type="form.medicine === 'other'? 'text': 'number'"></b-form-input>
      </b-form-group>
      <b-form-group v-if="form.type === 'massnahme_varroa_biotechnik'" label-for="biotech">
        <b-form-select id="biotech" :class="{ 'with-placeholder': !form.biotech }" v-model="form.biotech">
          <option :value="null" disabled>{{ $t('measure.form.biotech.label') }}</option>
          <option v-for="(text, value) in $t('measure.form.biotech.options')" :value="value" :key="value">{{ text }}</option>
        </b-form-select>
      </b-form-group>
      <b-form-group v-if="form.type === 'massnahme_varroa_biotechnik' && form.biotech === 'other'" label-for="other_biotech">
        <b-form-input :placeholder="$t('measure.form.other_biotech.label')" :class="{ 'with-placeholder': !form.other_biotech }" id="other_biotech" v-model="form.other_biotech"></b-form-input>
      </b-form-group>
      <b-form-group v-if="form.type === 'massnahme_schwarmverhinderung'" label-for="swarm_prevention">
        <b-form-select id="swarm_prevention" :class="{ 'with-placeholder': !form.swarm_prevention }" v-model="form.swarm_prevention">
          <option :value="null" disabled>{{ $t('measure.form.swarm_prevention.label') }}</option>
          <option v-for="(text, value) in $t('measure.form.swarm_prevention.options')" :value="value" :key="value">{{ text }}</option>
        </b-form-select>
      </b-form-group>
      <b-form-group v-if="form.type === 'massnahme_schwarmverhinderung' && form.swarm_prevention === 'other'" label-for="other_swarm_prevention">
        <b-form-input :placeholder="$t('measure.form.other_swarm_prevention.label')" :class="{ 'with-placeholder': !form.other_swarm_prevention }" id="other_swarm_prevention" v-model="form.other_swarm_prevention"></b-form-input>
      </b-form-group>
      <b-form-group v-if="form.type === 'massnahme_futtergabe'" label-for="feed">
        <b-form-select id="feed" :class="{ 'with-placeholder': !form.feed }" v-model="form.feed">
          <option :value="null" disabled>{{ $t('measure.form.feed.label') }}</option>
          <option v-for="(text, value) in $t('measure.form.feed.options')" :value="value" :key="value">{{ text }}</option>
        </b-form-select>
      </b-form-group>
      <b-form-group v-if="form.type === 'massnahme_futtergabe' && form.feed === 'other'" label-for="other_feed">
        <b-form-input :placeholder="$t('measure.form.other_feed.label')" :class="{ 'with-placeholder': !form.other_feed }" id="other_feed" v-model="form.other_feed"></b-form-input>
      </b-form-group>
      <b-form-group v-if="form.type === 'massnahme_futtergabe' && (form.feed === 'honeycomb' || form.feed === 'honey' || form.feed === 'feedbee' || form.feed === 'feedsyrup')" label-for="amount">
        <b-form-input :placeholder="$t('measure.form.amount.label_kg')" :class="{ 'with-placeholder': !form.amount }" id="amount" v-model="form.amount" type="number"></b-form-input>
      </b-form-group>
      <b-form-group v-else-if="form.type === 'massnahme_futtergabe' && (form.feed === 'sugarsolution_1_1' || form.feed === 'sugarsolution_3_2')" label-for="amount">
        <b-form-input :placeholder="$t('measure.form.amount.label_l')" :class="{ 'with-placeholder': !form.amount }" id="amount" v-model="form.amount" type="number"></b-form-input>
      </b-form-group>
      <b-form-group v-else-if="form.type === 'massnahme_futtergabe' && form.feed === 'other'" label-for="amount">
        <b-form-input :placeholder="$t('measure.form.amount.lable_other_feed')" :class="{ 'with-placeholder': !form.amount }" id="amount" v-model="form.amount" ></b-form-input>
      </b-form-group>
      <b-form-group v-if="form.type === 'massnahme_honigernte'" label-for="amount">
        <b-form-input :placeholder="$t('measure.form.amount.label_erntemenge_kg')" :class="{ 'with-placeholder': !form.amount }" id="amount" v-model="form.amount" type="number"></b-form-input>
      </b-form-group>
      <b-form-group v-if="form.type === 'massnahme_honigernte'" label-for="honey_type">
        <b-form-input :placeholder="$t('measure.form.honey_type.label')" :class="{ 'with-placeholder': !form.honey_type }" id="honey_type" v-model="form.honey_type"></b-form-input>
      </b-form-group>
      <b-form-group v-if="form.type === 'massnahme_raummanagement'" label-for="space_management">
        <b-form-select id="space_management" :class="{ 'with-placeholder': !form.space_management }" v-model="form.space_management">
          <option :value="null" disabled>{{ $t('measure.form.space_management.label') }}</option>
          <option v-for="(text, value) in $t('measure.form.space_management.options')" :value="value" :key="value">{{ text }}</option>
        </b-form-select>
      </b-form-group>
      <b-row v-if="loading">
        <b-col class="col-12 mb-3">
          <div class="d-flex justify-content-around flex-row mb-3"><b-spinner></b-spinner></div>
        </b-col>
      </b-row>
      <b-row class="">
        <b-col class="col-12 col-md-6 mb-3">
          <b-link class="bg-transparent h-100 text-primary border-primary border form-control text-center" :title="$t('measure.form.cancel')" :to="'/colony/' + colony + '/' + apiary">
            {{ $t('measure.form.cancel') }}
          </b-link>
        </b-col>
        <b-col class="col-12 col-md-6 mb-3">
          <b-button class="d-block w-100 h-100" type="submit" variant="primary">{{ $t('measure.form.submit') }}</b-button>
        </b-col>
      </b-row>
      <b-row v-if="id">
        <b-col class="col-12 col-md-6"></b-col>
        <b-col class="col-12 col-md-6">
          <div>
            <b-link v-if="id" class="bg-transparent text-primary border-primary border form-control text-center" :title="$t('measure.delete')" @click.prevent="clickDelete">
              {{ $t('measure.delete') }}
            </b-link>
            <b-modal id="confirmDelete" centered hide-header-close @ok="doDelete">
              <template slot="modal-title">
                {{ $t('measure.confirm_delete.title') }}
              </template>
              {{ $t('measure.confirm_delete.text') }}
              <template slot="modal-footer" slot-scope="{ ok, cancel }">
                <b-button size="lg" class="ml-auto" @click="cancel()">
                  {{ $t('measure.confirm_delete.no') }}
                </b-button>
                <b-button size="lg" class="mr-auto" @click="ok()">
                  {{ $t('measure.confirm_delete.yes') }}
                </b-button>
              </template>
            </b-modal>
          </div>
        </b-col>
      </b-row>
      <b-row v-if="!id">
        <b-col class="col-12 col-md-6 mb-3">
          <b-button @click="submitGlobal" class="d-block h-100 w-100 bg-transparent text-primary border-primary border" :title="$t('measure.form.submit_global')">
            {{ $t('measure.form.submit_global') }}
          </b-button>
        </b-col>
        <b-col class="col-12 col-md-6 mb-3">
          <b-button @click="submitAll" class="d-block h-100 w-100 bg-transparent text-primary border-primary border" :title="$t('measure.form.submit_all')">
            {{ $t('measure.form.submit_all') }}
          </b-button>
        </b-col>
      </b-row>
      <b-modal :id="'offLineModalMeasureDelete'" centered hide-header-close @ok="hideModal" class="network-modal">
        <template slot="modal-title">
          {{ $t('offLineModal.title') }}
        </template>
        {{ $t('offLineModal.text') }}
        <template slot="modal-footer" class="offline-modal-footer" slot-scope="{ ok }">
          <b-button size="lg" class="mt-3" @click="ok()">
            {{ $t('offLineModal.ok') }}
          </b-button>
        </template>
      </b-modal>
      <b-modal :id="'offLineModalMeasureSave'" centered hide-header-close @ok="hideModalSave" class="network-modal">
        <template slot="modal-title">
          {{ $t('offLineModal.title') }}
        </template>
        {{ $t('offLineModal.text') }}
        <template slot="modal-footer" class="offline-modal-footer" slot-scope="{ ok }">
          <b-button size="lg" class="mt-3" @click="ok()">
            {{ $t('offLineModal.ok') }}
          </b-button>
        </template>
      </b-modal>
    </template>
  </b-form>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import moment from 'moment'
import Datepicker from 'vuejs-datepicker'
import { de } from 'vuejs-datepicker/dist/locale'
// import MeasureDelete from './MeasureDelete'

export default {
  name: 'MeasureForm',
  props: ['id', 'colony', 'apiary', 'type'],
  components: {
    // MeasureDelete,
    Datepicker: Datepicker
  },
  data () {
    return {
      de: de,
      showError: false,
      showSaveMoreOneError: false,
      loading: false,
      loaded: false,
      form: {
        date: new Date(),
        type: null,
        diagnosis: null,
        mites: null,
        days: null,
        bee_mass: null,
        medicine: null,
        amount: null,
        other_medicine: null,
        biotech: null,
        other_biotech: null,
        swarm_prevention: null,
        other_swarm_prevention: null,
        feed: null,
        other_feed: null,
        honey_type: null,
        space_management: null
      },
      apiaryColonies: null
    }
  },
  methods: {
    // async loadColonies () {
    //   const vm = this
    //   try {
    //     this.loading = true
    //     this.apiaryColonies = (await this.$api.colonies.getAll()).filter(function (row) {
    //       if (row.apiary !== vm.apiary) {
    //         return false
    //       }
    //
    //       return (
    //         !row.field_massnahmen_und_aktionen ||
    //         Object.values(row.field_massnahmen_und_aktionen).filter(function (action) {
    //           return action.type === 'aktion_aufloesung' || action.type === 'aktion_vereinigung'
    //         }).length === 0
    //       )
    //     })
    //     this.loading = false
    //   } catch (e) { // network error etc.
    //     console.log(e)
    //     this.apiaryColonies = null
    //     this.loading = false
    //   }
    //
    //   this.showError = !this.apiaryColonies
    //   console.log(this.apiaryColonies)
    //   return this.apiaryColonies
    // },
    showOffLineModalSave () {
      this.$bvModal.show('offLineModalMeasureSave')
    },
    hideModalSave () {
      this.$bvModal.hide('offLineModalMeasureSave')
    },
    async submit () {
      if (navigator.onLine) {
        this.$v.form.$touch()
        if (!this.$v.$error) {
          this.loading = true
          const result = await this.saveOneMeasure(this.colony)
          this.loading = false
          this.showError = (!result ? 'save' : false)

          if (result) {
            this.$router.replace('/colony/' + this.colony + '/' + this.apiary)
          }

          return result
        } else {
          return false
        }
      } else {
        this.showOffLineModalSave()
      }
    },
    clickDelete () {
      if (navigator.onLine) {
        this.showModal()
      } else {
        this.showOffLineModal()
      }
    },
    showModal () {
      this.$bvModal.show('confirmDelete')
    },
    showOffLineModal () {
      this.$bvModal.show('offLineModalMeasureDelete')
    },
    hideModal () {
      this.$bvModal.hide('offLineModalMeasureDelete')
    },
    async doDelete () {
      this.loading = true
      const token = await this.$api.user.getToken()
      const result = await this.$api.measures.deleteItem({ id: this.id, colony: this.colony, token })
      this.loading = false
      // console.log(result)
      if (result) {
        this.$router.replace('/colony/' + this.colony + '/' + this.apiary)
      }
      return result
    },
    // onClickDelete (value) {
    //   if (value === 'Yes') {
    //     this.loading = true
    //   } else {
    //     this.loading = false
    //     this.$router.go(-1)
    //   }
    // },
    async submitGlobal () {
      this.$v.form.$touch()
      if (!this.$v.$error) {
        try {
          this.loading = true
          const token = await this.$api.user.getToken()
          const colonies = await this.$api.colonies.getAll()
          const result = await Promise.all(colonies.map(async (colony) => {
            const newColony = await this.saveMeasure(colony.id, token)
            console.log(newColony)
            return newColony
          }))
          this.loading = false
          this.showError = (!result ? 'save' : false)

          if (result) {
            this.$router.replace('/colony/' + this.colony + '/' + this.apiary)
          }

          return result
        } catch (e) {
          this.showError = 'save'
          return false
        }
      } else {
        return false
      }
    },
    async submitAll () {
      this.$v.form.$touch()
      if (!this.$v.$error) {
        try {
          this.loading = true
          const token = await this.$api.user.getToken()
          const colonies = await this.$api.colonies.getAll()
          const result = await Promise.all(colonies.filter((colony) => colony.apiary === this.apiary).map(async (colony) => {
            const newColony = await this.saveMeasure(colony.id, token)
            console.log(newColony)
            return newColony
          }))
          this.loading = false
          this.showError = (!result ? 'save' : false)
          console.log('Result : ', result)
          if (result) {
            this.$router.replace('/colony/' + this.colony + '/' + this.apiary)
          }

          // return result
        } catch (e) {
          this.showError = 'save'
          return false
        }
      } else {
        return false
      }
    },
    async saveMeasure (colonyId, token) {
      try {
        const result = await this.$api.measures.save({
          id: this.id,
          colony: colonyId,
          date: moment(this.form.date).format('YYYY[-]MM[-]DD'),
          type: this.form.type,
          diagnosis: this.form.diagnosis,
          mites: this.form.mites,
          days: this.form.days,
          beeMass: this.form.bee_mass,
          medicine: this.form.medicine,
          amount: this.form.amount,
          otherMedicine: this.form.other_medicine,
          biotech: this.form.biotech,
          otherBiotech: this.form.other_biotech,
          swarmPrevention: this.form.swarm_prevention,
          otherSwarmPrevention: this.form.other_swarm_prevention,
          feed: this.form.feed,
          otherFeed: this.form.other_feed,
          honeyType: this.form.honey_type,
          spaceManagement: this.form.space_management,
          token
        })
        return result
      } catch (e) { // network error etc.
        if (e.response && e.response.status === 403) {
          this.showSaveMoreOneError = true
        }
        return false
      }
    },
    async saveOneMeasure (colonyId) {
      try {
        this.loading = true
        const token = await this.$api.user.getToken()
        const result = await this.$api.measures.save({
          id: this.id,
          colony: colonyId,
          date: moment(this.form.date).format('YYYY[-]MM[-]DD'),
          type: this.form.type,
          diagnosis: this.form.diagnosis,
          mites: this.form.mites,
          days: this.form.days,
          beeMass: this.form.bee_mass,
          medicine: this.form.medicine,
          amount: this.form.amount,
          otherMedicine: this.form.other_medicine,
          biotech: this.form.biotech,
          otherBiotech: this.form.other_biotech,
          swarmPrevention: this.form.swarm_prevention,
          otherSwarmPrevention: this.form.other_swarm_prevention,
          feed: this.form.feed,
          otherFeed: this.form.other_feed,
          honeyType: this.form.honey_type,
          spaceManagement: this.form.space_management,
          token
        })
        this.loading = false
        return result
      } catch (e) { // network error etc.
        if (e.response && e.response.status === 403) {
          this.$router.replace('/login')
        }

        return false
      }
    },
    async loadData () {
      if (this.id) {
        try {
          const data = await this.$api.measures.getOne(this.id)
          this.form.date = (data.date && Date.parse(data.date)) || this.form.date
          this.form.type = data.type || this.form.type
          this.form.diagnosis = data.diagnosis || this.form.diagnosis
          this.form.mites = data.mites || this.form.mites
          this.form.days = data.days || this.form.days
          this.form.bee_mass = data.bee_mass || this.form.bee_mass
          this.form.medicine = data.medicine || this.form.medicine
          this.form.amount = data.amount || this.form.amount
          this.form.other_medicine = data.other_medicine || this.form.other_medicine
          this.form.biotech = data.biotech || this.form.biotech
          this.form.other_biotech = data.other_biotech || this.form.other_biotech
          this.form.swarm_prevention = data.swarm_prevention || this.form.swarm_prevention
          this.form.other_swarm_prevention = data.other_swarm_prevention || this.form.other_swarm_prevention
          this.form.feed = data.feed || this.form.feed
          this.form.other_feed = data.other_feed || this.form.other_feed
          this.form.honey_type = data.honey_type || this.form.honey_type
          this.form.space_management = data.space_management || this.form.space_management
          if (data) {
            this.loaded = true
          } else {
            this.showError = 'not found'
          }
          // await this.loadColonies()
          return data
        } catch (e) {
          this.showError = 'not found'
        }
      } else {
        // await this.loadColonies()
        this.loaded = true
      }

      return null
    }
  },
  validations: {
    form: {
      date: {
        required
      }
    }
  },
  mounted () {
    if (this.type) {
      this.form.type = this.type
    }
    this.loadData()
  },
  computed: {
    mitesDayPercentage () {
      if (this.form.mites && this.form.days) {
        let mites = this.form.mites.replace(/[^\d.]/g, '')
        let days = this.form.days.replace(/[^\d.]/g, '')
        if (!isNaN(mites) && !isNaN(days)) {
          let percent = parseFloat((mites / days).toFixed(1))
          return this.$t('measure.form.mites_day_percentage.info_msg', { percent: percent })
          // return percent
        } else {
          return null
        }
      } else { return null }
    },
    mitesDayHasProblem () {
      const vm = this
      if (this.form.mites && this.form.days && this.form.date) {
        let mites = this.form.mites.replace(/[^\d.]/g, '')
        let days = this.form.days.replace(/[^\d.]/g, '')
        if (!isNaN(mites) && !isNaN(days) && parseFloat(days) > 0) {
          let percent = parseFloat((mites / days).toFixed(1))
          const date = new Date(vm.form.date)
          let month = date.getMonth()
          let day = date.getDate()
          // 01.05. - 31.08.
          if (month >= 4 && month <= 7) {
            return percent >= 5
            // 01.09. - 21.12.
          } else if ((month >= 8 && month <= 10) || (month === 11 && day <= 21)) {
            return percent >= 0.5
            // 22.12. - 30.04.
          } else {
            return percent >= 1
          }
        } else {
          return false
        }
      } else { return false }
    },
    mitesBeeMassPercentage () {
      if (this.form.mites && this.form.bee_mass) {
        let mites = this.form.mites.replace(/[^\d.]/g, '')
        let beeMass = this.form.bee_mass.replace(/[^\d.]/g, '')
        if (!isNaN(mites) && !isNaN(beeMass) && (parseFloat(beeMass) > 0)) {
          let percent = parseFloat((mites / beeMass * 10).toFixed(1))
          return this.$t('measure.form.mites_bee_mass_percentage.info_msg', { percent: percent })
          // return percent
        } else {
          return null
        }
      } else { return null }
    },
    mitesBeeMassHasProblem () {
      const vm = this
      if (this.form.mites && this.form.bee_mass && this.form.date) {
        let mites = this.form.mites.replace(/[^\d.]/g, '')
        let beeMass = this.form.bee_mass.replace(/[^\d.]/g, '')
        if (!isNaN(mites) && !isNaN(beeMass) && parseFloat(beeMass) > 0) {
          let percent = parseFloat((mites / beeMass * 10).toFixed(1))
          const date = new Date(vm.form.date)
          let month = date.getMonth()
          // 01.05. - 31.08.
          if (month >= 4 && month <= 7) {
            if (percent < 1) return 0 // no Problem
            else return 1 // has problem
          } else {
            return 2 // kein Beurteilung möglich
          }
        } else {
          return false
        }
      } else { return false }
    }
  }
}
</script>

<style scoped>
  form {
    max-width: 500px;
    font-family: "Roboto Condensed", sans-serif;
  }
</style>
